import { defineStore } from 'pinia'
import config from '../config/env'

export type UserRole = 'admin' | 'user'

interface MemberDetails {
  title?: string
  memberNo?: string
  gender?: string
  memberProfileImage?: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  mobileNo?: string
  approvalStatus?: string
}

interface AuthState {
  token: string | null
  tokenExpiry: number | null
  lastActivity: number | null
  sessionTimeoutId: number | null
  userRole: UserRole | null
  userId: string | null
  memberDetails: MemberDetails | null
}

const SESSION_TIMEOUT = config.session.timeout

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem('token'),
    tokenExpiry: Number(localStorage.getItem('tokenExpiry')) || null,
    lastActivity: Number(localStorage.getItem('lastActivity')) || Date.now(),
    sessionTimeoutId: null,
    userRole: (localStorage.getItem('userRole') as UserRole) || null,
    userId: localStorage.getItem('userId') || null,
    memberDetails: JSON.parse(localStorage.getItem('memberDetails') || 'null'),
  }),

  getters: {
    isAuthenticated(): boolean {
      if (!this.token || !this.tokenExpiry) {
        return false
      }
      // Check if token is expired
      const tokenValid = this.tokenExpiry > Date.now()
      // Check if session is still active (within 15 minutes of last activity)
      const sessionActive = this.lastActivity ? Date.now() - this.lastActivity < SESSION_TIMEOUT : false
      return tokenValid && sessionActive
    },

    isTokenExpired(): boolean {
      if (!this.token || !this.tokenExpiry) return true
      return this.tokenExpiry < Date.now()
    },

    isSessionExpired(): boolean {
      if (!this.lastActivity) return true
      return Date.now() - this.lastActivity >= SESSION_TIMEOUT
    },

    // Get member first name
    memberFirstName(): string {
      if (!this.memberDetails) return 'Unknown User'
      const firstName = this.memberDetails.firstName || ''
      return `${firstName}`.trim() || 'Unknown User'
    },

    // Get member full name for display
    memberFullName(): string {
      if (!this.memberDetails) return 'Unknown User'
      const firstName = this.memberDetails.firstName || ''
      const lastName = this.memberDetails.lastName || ''
      return `${firstName} ${lastName}`.trim() || 'Unknown User'
    },

    // Get member initials
    memberInitials(): string {
      if (!this.memberDetails) return 'JD'
      const first = this.memberDetails.firstName?.charAt(0).toUpperCase() || 'J'
      const last = this.memberDetails.lastName?.charAt(0).toUpperCase() || 'D'
      return `${first}${last}`
    },

    // Check if user is admin
    isAdmin(): boolean {
      return this.userRole === 'admin'
    },
  },

  actions: {
    setAuthData(token: string, expiresIn: number, userRole: UserRole, userId: string) {
      this.token = token
      this.tokenExpiry = Date.now() + expiresIn * 1000 // Convert seconds to milliseconds
      this.lastActivity = Date.now()
      this.userRole = userRole
      this.userId = userId

      // Store in localStorage
      localStorage.setItem('token', token)
      localStorage.setItem('tokenExpiry', this.tokenExpiry.toString())
      localStorage.setItem('lastActivity', this.lastActivity.toString())
      localStorage.setItem('userRole', userRole)
      localStorage.setItem('userId', userId)

      // Setup session timeout
      this.setupSessionTimeout()
    },

    setMemberDetails(memberDetails: MemberDetails) {
      this.memberDetails = memberDetails
      localStorage.setItem('memberDetails', JSON.stringify(memberDetails))
    },

    // Helper method to extract user role from roles array
    extractUserRole(roles: string[]): UserRole {
      // Check roles in priority order (case-insensitive)
      const roleString = roles.join(',').toLowerCase()

      if (roleString.includes('admin')) {
        return 'admin'
      } else if (roleString.includes('member') || roleString.includes('user')) {
        return 'user'
      }

      // Default to user if no recognized role
      return 'user'
    },

    updateActivity() {
      if (this.token) {
        this.lastActivity = Date.now()
        localStorage.setItem('lastActivity', this.lastActivity.toString())
        // Reset session timeout
        this.setupSessionTimeout()
      }
    },

    setupSessionTimeout() {
      // Clear existing timeout
      if (this.sessionTimeoutId) {
        clearTimeout(this.sessionTimeoutId)
      }

      // Set new timeout
      this.sessionTimeoutId = window.setTimeout(() => {
        if (this.isSessionExpired) {
          this.logout()
        }
      }, SESSION_TIMEOUT)
    },

    checkTokenExpiry(): boolean {
      if (this.isTokenExpired || this.isSessionExpired) {
        this.logout()
        return false
      }
      return true
    },

    logout() {
      // Clear timeout
      if (this.sessionTimeoutId) {
        clearTimeout(this.sessionTimeoutId)
        this.sessionTimeoutId = null
      }

      // Clear state
      this.token = null
      this.tokenExpiry = null
      this.lastActivity = null
      this.userRole = null
      this.userId = null
      this.memberDetails = null

      // Clear localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('tokenExpiry')
      localStorage.removeItem('lastActivity')
      localStorage.removeItem('userRole')
      localStorage.removeItem('userId')
      localStorage.removeItem('memberDetails')

      // Redirect to login if router is available
      const router = (globalThis as any).__router__
      if (router && router.currentRoute.value.name !== 'login') {
        router.push({ name: 'login' })
      }
    },

    getUserRole() {
      return this.userRole
    },

    initializeAuth() {
      // Check if session is still valid on app startup
      if (this.token && (this.isTokenExpired || this.isSessionExpired)) {
        this.logout()
        return false
      }

      // Setup session timeout if authenticated
      if (this.token) {
        this.setupSessionTimeout()
        return true
      }

      return false
    },
  },
})

// Add type exports
export type AuthStore = ReturnType<typeof useAuthStore>
export type { MemberDetails }
